/**
 * **Plans on this quote** — `docs/proposal.md` §10.5 (board 14, cell 4).
 *
 * A row per visit, a column per plan, a checkbox in each cell. It is the one
 * surface where the shape of a ladder is visible at a glance: Silver's column
 * stops after two ticks, Gold's after three, and the visit the customer is
 * actually choosing between is the row where they differ.
 *
 * **A plan's price is never typed.** It is the sum of its visits, so moving a
 * visit moves both totals — which is the whole argument for editing the
 * memberships here rather than on a price field somebody has to remember to
 * keep in step.
 */
import { useState } from 'react'
import type { MarginChoice, QuoteTree, QuoteVisitView } from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { formatDollars } from '../lib/format.js'
import { Button, buttonClass } from '../components/primitives.js'
import { CheckRow } from '../components/form/rows.js'
import { FieldShell } from '../components/fields.js'
import { ContextMenu, type MenuItem } from '../components/context-menu.js'
import { MenuButton } from '../components/menu-select.js'
import { IconCheck, IconChevronDown, IconGrip, IconLock, IconPlus, IconTag } from '../frame/icons.js'
import { IconVisit } from '../builder/rows/glyphs.js'
import { visitKindGlyph } from '../builder/fields/kind-field.js'
import { CollapseCaret } from '../builder/rows/caret.js'
import { RowMenuButton } from '../builder/rows/add-row.js'
import { VisitTag } from '../builder/rows/visit-card.js'
import { EditorActions, EditorHeader, EditorInput, EditorShell } from '../builder/editor-shell.js'
import {
  ADD_CHOICE, ADD_VISITS, CHOICE_BOX_EMPTY, CHOICE_BOX_RENAME, CHOICE_SETTINGS, CHOICE_SETTINGS_FACTS,
  CHOOSE_NONE, CHOOSE_NONE_SUB, FIND_A_VISIT, GO_TO_VISIT, NEW_CHOICE, NEW_CHOICE_FACTS,
  NOT_ON_PROPOSAL, NOT_ON_PROPOSAL_WHY, OPTION_SETTINGS_FACTS, PICKS_ONE, PLAN_ROW_COST,
  PLAN_ROW_MARGIN, PLAN_ROW_PRICE, PLAN_ROW_VISITS, RECOMMENDED, RECOMMENDED_SUB, REMOVE_CHOICE,
  REMOVE_CHOICE_BOX, REMOVE_FROM_BOX, VISITS_IN_THE_PLAN,
} from '../builder/copy.js'

export interface PlansGridProps {
  group: QuoteTree['chooseGroups'][number]
  options: QuoteTree['chooseOptions']
  visits: QuoteVisitView[]
  canEdit: boolean
  /** `quoteMargin().byChoice` for this question: Visits, Price, Cost, Margin (§9.2). */
  choice?: MarginChoice
  targetPct: number
  /** The whole set for one visit, never a delta (§10.5). */
  onMembership: (visitId: string, optionIds: string[]) => void
  /** **Add choice** (step 22 item 2.1): the choice's own popup, empty, so it can arrive recommended. */
  onAddPlan: (name: string, recommended: boolean) => void
  onRename: (optionId: string, name: string) => void
  /** Recommended on, or off again — the choice popup's checkbox (step 21 item 4.2). */
  onRecommend: (optionId: string, recommended: boolean) => void
  onRemovePlan: (optionId: string, keepVisits: boolean) => void
  /** The question's Customer may choose none; absent where it cannot be stored (a package). */
  onAllowNone?: (allow: boolean) => void
  /** Rename the question itself — the heading over the plans on the proposal. */
  onReword: (name: string) => void
  /** Take the whole choice box away; its visits stay on the quote (step 21 item 4.4). */
  onRemoveGroup?: () => void
  /**
   * **+ Add visits ▾** (step 21 item 4.5, step 22 item 2.4): the quote's
   * visits that are in no question, in the related-dropdown style, and
   * `＋ Create a new visit` last. Either joins the box **in no choice** — the
   * estimator ticks where it belongs. Absent where there is nowhere for it to
   * go (the package builder adds its visits its own way).
   */
  addable?: { id: string; name: string; sub: string; total: number; role?: QuoteVisitView['role'] }[]
  /** An existing visit by id, or `null` for a new one named by what was typed. */
  onAddVisit?: (visitId: string | null, name: string) => void
  /**
   * Take a visit out of the box (step 22 item 2.3). Unticking every choice no
   * longer does that — a visit may sit in the box in no choice — so each row's
   * ⋮ is the way out. Absent in the package builder, where no plan ticked
   * means every plan.
   */
  onLeave?: (visitId: string) => void
  /** **Go to visit** on a row's ⋮. */
  onGoTo?: (visitId: string) => void
  /** Folded to its header — the caret, and Collapse all (step 21 item 4.3). */
  collapsed?: boolean
  onToggle?: () => void
  /** Every visit on the quote, in the order the grid's rows now say (rows drag). */
  onReorder: (visitIds: string[]) => void
}

/** The choice tone — the same purple pair the visit card's chips wear. */
const CHOICE_FG = 'rgb(var(--status-quoteLine-deployment-fg))'
const CHOICE_BG = 'rgb(var(--status-quoteLine-deployment-bg))'

export function PlansGrid(props: PlansGridProps) {
  const { group, options, visits, canEdit, choice, targetPct, collapsed = false } = props
  const here = options.filter((one) => one.groupId === group.id && !one.declined)
  const rows = visits.filter((visit) => visit.chooseGroupId === group.id)
  /** Where **Add choice** was pressed: its popup hangs there (step 22 item 2.1). */
  const [adding, setAdding] = useState<HTMLElement | null>(null)
  /** The ⋮ menus — a row's, or the box's own (step 22 item 2.3). */
  const [menu, setMenu] = useState<{ x: number; y: number; items: MenuItem[] } | null>(null)
  /** The question's settings popup, hung off its title (step 21 item 4.1). */
  const [settingsAt, setSettingsAt] = useState<HTMLElement | null>(null)
  /** The title, which the ⋮'s Rename and Add choice hang their popups off. */
  const [titleRef, setTitleRef] = useState<HTMLElement | null>(null)
  /** Which choice's popup is open, and where (step 21 item 4.2). */
  const [column, setColumn] = useState<{ id: string; at: HTMLElement } | null>(null)
  /** Which plan is being removed, and so what to do with its lone visits. */
  const [removing, setRemoving] = useState<string | null>(null)
  const [dragging, setDragging] = useState<string | null>(null)

  const membership = (visit: QuoteVisitView, optionId: string, on: boolean) => {
    const was = here.filter((one) => one.visitIds.includes(visit.id)).map((one) => one.id)
    props.onMembership(visit.id,
      on ? [...was, optionId] : was.filter((id) => id !== optionId))
  }

  /* The visits only this plan holds, which is what the confirm is about. */
  const onlyIn = (optionId: string) => rows.filter((visit) =>
    here.every((one) => one.id === optionId || !one.visitIds.includes(visit.id))
    && here.some((one) => one.id === optionId && one.visitIds.includes(visit.id)))

  const figures = (optionId: string) => choice?.options.find((one) => one.id === optionId)
  const floor = choice?.options.find((one) => one.inBase)?.name ?? here[0]?.name ?? ''

  /* A row dropped on another: the whole quote's order, with this one moved. */
  const drop = (onto: string) => {
    if (!dragging || dragging === onto) return
    const order = visits.map((visit) => visit.id).filter((id) => id !== dragging)
    order.splice(order.indexOf(onto), 0, dragging)
    props.onReorder(order)
    setDragging(null)
  }

  const cell = 'px-3 text-center'
  /** A trailing column for each row's ⋮ — only where a row has somewhere to go. */
  const menuColumn = Boolean(props.onLeave)
  const label = 'sticky left-0 z-10 px-5 text-left'
  const open = column ? here.find((one) => one.id === column.id) ?? null : null

  return (
    <section data-plans-card={group.id}
      className="overflow-hidden rounded-record border border-line bg-surface">
      {/*
        * The visit card's header to the pixel (step 21 item 4.4): px-3.5,
        * py-3, a caret, then the 40px tile a little in from it — so a choice
        * box and the visits under it read as one kind of box.
        */}
      <header className="flex items-center gap-3 px-3.5 py-3">
        {props.onToggle && (
          <CollapseCaret collapsed={collapsed} onToggle={props.onToggle} label={group.name} />
        )}
        <span aria-hidden className="ml-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-[12px]"
          style={{ background: CHOICE_BG, color: CHOICE_FG }}>
          <IconTag size={18} />
        </span>
        <span className="flex min-w-0 flex-col gap-[3px]">
          {canEdit
            ? (
              <button
                ref={setTitleRef}
                type="button"
                data-choice-title=""
                aria-haspopup="dialog"
                aria-expanded={settingsAt !== null}
                onClick={(event) => setSettingsAt(event.currentTarget)}
                className="truncate text-left text-[15px] font-bold text-text hover:text-accent"
              >
                {group.name}
              </button>
            )
            : <b className="truncate text-[15px] font-bold text-text">{group.name}</b>}
          <span className="truncate text-body text-text-muted">
            {here.length === 0 ? CHOICE_BOX_EMPTY : PICKS_ONE(floor)}
          </span>
        </span>
        {canEdit && (
          <span className="ml-auto flex shrink-0 items-center gap-2">
            {props.onAddVisit && here.length > 0 && (
              <MenuButton
                options={(props.addable ?? []).map((visit) => ({
                  value: visit.id,
                  label: visit.name || 'Untitled visit',
                  help: visit.sub,
                  detail: formatDollars(visit.total),
                  /*
                   * The visit's own tile, at 28 — it was the menu's grey
                   * square with nothing in it (Aaron, step 23 item 1.1).
                   */
                  glyph: visit.role ? visitKindGlyph(visit.role, 15) : <IconVisit size={15} />,
                  tileTone: 'accent' as const,
                }))}
                onPick={(visitId) => props.onAddVisit?.(visitId, '')}
                onCreate={(name) => props.onAddVisit?.(null, name)}
                createThing="visit"
                searchPlaceholder={FIND_A_VISIT}
                minWidth={320}
                className={buttonClass()}
              >
                <IconPlus size={14} /> {ADD_VISITS} <IconChevronDown size={13} />
              </MenuButton>
            )}
            <Button onClick={(event) => setAdding(event.currentTarget)}>
              <IconPlus size={14} /> {ADD_CHOICE}
            </Button>
            {/* The box's own ⋮, as every visit and package on this page has (step 22 item 2.3). */}
            <RowMenuButton label={`${group.name} options`} onOpen={(at) => setMenu({
              ...at,
              items: [
                { key: 'rename', label: CHOICE_BOX_RENAME, onSelect: () => setSettingsAt(titleRef) },
                {
                  key: 'add',
                  label: ADD_CHOICE,
                  onSelect: () => setAdding(titleRef),
                },
                ...(props.onToggle
                  ? [{ key: 'fold', label: collapsed ? 'Expand' : 'Collapse', onSelect: props.onToggle }]
                  : []),
                ...(props.onRemoveGroup
                  ? [{
                    key: 'remove',
                    label: REMOVE_CHOICE_BOX,
                    tone: 'danger' as const,
                    separatorBefore: true,
                    onSelect: props.onRemoveGroup,
                  }]
                  : []),
              ],
            })} />
          </span>
        )}
      </header>

      {settingsAt && (
        <ChoiceSettings
          group={group}
          anchor={settingsAt}
          onClose={() => setSettingsAt(null)}
          onRename={props.onReword}
          onAllowNone={props.onAllowNone}
          onRemove={props.onRemoveGroup}
        />
      )}

      {adding && (
        <OptionSettings
          option={null}
          boxName={group.name}
          anchor={adding}
          onClose={() => setAdding(null)}
          onSave={(name, recommended) => props.onAddPlan(name, recommended)}
        />
      )}

      {menu && <ContextMenu items={menu.items} x={menu.x} y={menu.y} onClose={() => setMenu(null)} />}

      {/*
        * **The visit column stays put while the plans scroll** (§10.5): four
        * plans do not fit in 390px, and a row whose name has scrolled off the
        * left is a row nobody can tick. `sticky left-0` on every first cell.
        */}
      {!collapsed && here.length > 0 && (
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-left">
          <thead>
            <tr className="border-y border-line-soft bg-surface-alt">
              <th className={cn(label, 'h-11 bg-surface-alt text-label uppercase text-text-faint')}>
                {VISITS_IN_THE_PLAN}
              </th>
              {here.map((option) => (
                <th key={option.id} className={cn(cell, 'h-11 min-w-[120px] align-middle')}>
                  <button
                    type="button"
                    disabled={!canEdit}
                    aria-haspopup="dialog"
                    aria-expanded={column?.id === option.id}
                    onClick={(event) => setColumn(column?.id === option.id
                      ? null
                      : { id: option.id, at: event.currentTarget })}
                    className="inline-flex items-center gap-1.5 text-[14px] font-semibold"
                    style={{ color: CHOICE_FG }}
                  >
                    {option.name}
                    {/* The visit header's chip (step 22 item 2.5), not a pill of its own. */}
                    {option.recommended && <VisitTag tone="accent">{RECOMMENDED}</VisitTag>}
                  </button>
                </th>
              ))}
              {menuColumn && <th aria-hidden className="w-11" />}
            </tr>
          </thead>
          <tbody>
            {rows.map((visit) => (
              <tr
                key={visit.id}
                draggable={canEdit}
                onDragStart={() => setDragging(visit.id)}
                onDragOver={(event) => { if (dragging) event.preventDefault() }}
                onDrop={() => drop(visit.id)}
                className={cn('border-b border-line-soft', dragging === visit.id && 'opacity-60')}
              >
                <td className={cn(label, 'h-[61px] bg-surface')}>
                  <span className="flex items-center gap-3">
                    <span aria-hidden className="cursor-grab text-line-strong"><IconGrip size={14} /></span>
                    <b className="text-[15px] font-bold text-text">{visit.name || 'Untitled visit'}</b>
                    <span className="tnum whitespace-nowrap text-[13px] text-text-muted">
                      × {Number.isFinite(visit.occurrences) ? visit.occurrences : '∞'} · {formatDollars(visit.total)}
                    </span>
                    {/*
                      * In the box and in no choice (step 22 item 2.2): it answers
                      * nothing yet, so it is in no price and not on the page.
                      */}
                    {props.onLeave && !here.some((one) => one.visitIds.includes(visit.id)) && (
                      <span title={NOT_ON_PROPOSAL_WHY} data-not-on-proposal="">
                        <VisitTag>{NOT_ON_PROPOSAL}</VisitTag>
                      </span>
                    )}
                  </span>
                </td>
                {here.map((option) => {
                  const on = option.visitIds.includes(visit.id)
                  return (
                    <td key={option.id} className={cell}>
                      <button
                        type="button"
                        role="checkbox"
                        aria-checked={on}
                        aria-label={`${visit.name} in ${option.name}`}
                        disabled={!canEdit}
                        onClick={() => membership(visit, option.id, !on)}
                        className="mx-auto flex h-[26px] w-[26px] items-center justify-center rounded-full border-[1.5px]"
                        style={on
                          ? { background: CHOICE_FG, borderColor: CHOICE_FG, color: 'rgb(var(--color-surface))' }
                          : { borderColor: 'rgb(var(--color-line-strong))' }}
                      >
                        {on && <IconCheck size={14} />}
                      </button>
                    </td>
                  )
                })}
                {menuColumn && (
                  <td className="w-11 pr-3">
                    {canEdit && (
                      <RowMenuButton label={`${visit.name || 'Untitled visit'} options`} onOpen={(at) => setMenu({
                        ...at,
                        items: [
                          ...(props.onGoTo
                            ? [{ key: 'go', label: GO_TO_VISIT, onSelect: () => props.onGoTo?.(visit.id) }]
                            : []),
                          {
                            key: 'leave',
                            label: REMOVE_FROM_BOX,
                            tone: 'danger' as const,
                            separatorBefore: Boolean(props.onGoTo),
                            onSelect: () => props.onLeave?.(visit.id),
                          },
                        ],
                      })} />
                    )}
                  </td>
                )}
              </tr>
            ))}
            {/* Trips, not visit rows: Silver is five treatments (§9.2, `byChoice[].visits`). */}
            <FooterRow name={PLAN_ROW_VISITS} pad={menuColumn}
              cells={here.map((option) => String(figures(option.id)?.visits ?? option.visitIds.length))} />
            <FooterRow name={PLAN_ROW_PRICE} bold pad={menuColumn}
              cells={here.map((option) => formatDollars(figures(option.id)?.sold ?? option.total))} />
            <FooterRow name={PLAN_ROW_COST} internal lock pad={menuColumn}
              cells={here.map((option) => {
                const one = figures(option.id)
                return one ? formatDollars(one.cost) : '—'
              })} />
            <FooterRow name={PLAN_ROW_MARGIN} internal pad={menuColumn}
              cells={here.map((option) => {
                const pct = figures(option.id)?.marginPct ?? null
                return pct === null ? '—' : `${pct.toFixed(1)}%`
              })}
              warn={here.map((option) => {
                const pct = figures(option.id)?.marginPct ?? null
                return pct !== null && pct < targetPct
              })} />
          </tbody>
        </table>
      </div>
      )}

      {open && column && (
        <OptionSettings
          key={open.id}
          option={open}
          anchor={column.at}
          onClose={() => setColumn(null)}
          onSave={(name, recommended) => {
            if (name && name !== open.name) props.onRename(open.id, name)
            if (recommended !== open.recommended) props.onRecommend(open.id, recommended)
          }}
          onRemove={() => { setRemoving(open.id); setColumn(null) }}
        />
      )}

      {/*
        * **Removing a plan asks what to do with the visits only it held.**
        * Keeping them is the default and it is not a tidy-up: they become
        * part of every price, which is a change to the quote's total.
        */}
      {removing !== null && (
        <div className="border-t border-line-soft bg-warn-bg px-4 py-3">
          <p className="m-0 text-body text-text">
            {onlyIn(removing).length === 0
              ? 'Remove this plan? Every visit in it is also in another.'
              : `${onlyIn(removing).length === 1
                ? '1 visit is'
                : `${onlyIn(removing).length} visits are`} only in this plan.`}
          </p>
          <div className="mt-2 flex gap-2">
            <Button
              variant="primary"
              onClick={() => { props.onRemovePlan(removing, true); setRemoving(null) }}
            >
              Keep them in the quote
            </Button>
            <Button
              variant="danger"
              onClick={() => { props.onRemovePlan(removing, false); setRemoving(null) }}
            >
              Remove them too
            </Button>
            <Button onClick={() => setRemoving(null)}>Cancel</Button>
          </div>
        </div>
      )}
    </section>
  )
}

/**
 * The question's own popup (step 21 item 4.1): its name — the heading over
 * the choices on the proposal — whether the customer may choose none, and
 * Remove along the foot like every editor on this screen. It replaced a
 * Reword button that opened an inline box nobody could guess the purpose of.
 */
function ChoiceSettings({ group, anchor, onClose, onRename, onAllowNone, onRemove }: {
  group: QuoteTree['chooseGroups'][number]
  anchor: HTMLElement
  onClose: () => void
  onRename: (name: string) => void
  onAllowNone?: (allow: boolean) => void
  onRemove?: () => void
}) {
  const [name, setName] = useState(group.name)
  const [none, setNone] = useState(group.allowNone)
  const done = () => {
    if (name.trim() && name.trim() !== group.name) onRename(name.trim())
    if (onAllowNone && none !== group.allowNone) onAllowNone(none)
    onClose()
  }
  return (
    <EditorShell
      open
      onClose={onClose}
      anchor={anchor}
      label={CHOICE_SETTINGS}
      header={<EditorHeader kind="deployment" icon={IconTag} name={group.name} facts={CHOICE_SETTINGS_FACTS} />}
      footer={(
        <EditorActions
          onRemove={onRemove ? () => { onRemove(); onClose() } : undefined}
          removeLabel={REMOVE_CHOICE_BOX}
          onDone={done}
          disabled={!name.trim()}
        />
      )}
    >
      <div data-choice-settings="" className="flex flex-col gap-3.5">
        <FieldShell label="Name" hint="The heading over the choices on the proposal.">
          <EditorInput
            autoFocus
            aria-label="Choice box name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => { if (event.key === 'Enter' && name.trim()) done() }}
          />
        </FieldShell>
        {onAllowNone && (
          <CheckRow label={CHOOSE_NONE} sub={CHOOSE_NONE_SUB} checked={none} onChange={setNone} />
        )}
      </div>
    </EditorShell>
  )
}

/**
 * One choice's popup (step 21 item 4.2): its name, Recommended as a checkbox
 * like every other on/off on this screen, and Remove along the foot.
 *
 * **Add choice is the same popup, empty** (step 22 item 2.1): Name and
 * Recommended, with **Add choice** where Done is and nothing to remove. It
 * was an inline box that could only take a name, so a new choice could not
 * arrive recommended and looked nothing like the popup that edits it.
 */
function OptionSettings({ option, boxName, anchor, onClose, onSave, onRemove }: {
  /** Null: a new choice. */
  option: QuoteTree['chooseOptions'][number] | null
  /** The question it answers, for a new one's facts line. */
  boxName?: string
  anchor: HTMLElement
  onClose: () => void
  onSave: (name: string, recommended: boolean) => void
  onRemove?: () => void
}) {
  const [name, setName] = useState(option?.name ?? '')
  const [recommended, setRecommended] = useState(option?.recommended ?? false)
  const done = () => { if (name.trim()) { onSave(name.trim(), recommended); onClose() } }
  const title = option?.name ?? NEW_CHOICE
  return (
    <EditorShell
      open
      onClose={onClose}
      anchor={anchor}
      label={title}
      header={<EditorHeader kind="deployment" icon={IconTag} name={title}
        facts={option ? OPTION_SETTINGS_FACTS(option.visitIds.length) : NEW_CHOICE_FACTS(boxName ?? '')} />}
      footer={(
        <EditorActions
          onRemove={option ? onRemove : undefined}
          removeLabel={REMOVE_CHOICE}
          onDone={done}
          doneLabel={option ? undefined : ADD_CHOICE}
          disabled={!name.trim()}
        />
      )}
    >
      <div data-option-settings="" className="flex flex-col gap-3.5">
        <FieldShell label="Name">
          <EditorInput
            autoFocus
            aria-label={option ? `Choice name, ${option.name}` : 'Choice name'}
            value={name}
            placeholder={option ? undefined : 'Gold'}
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => { if (event.key === 'Enter' && name.trim()) done() }}
          />
        </FieldShell>
        <CheckRow label={RECOMMENDED} sub={RECOMMENDED_SUB} checked={recommended} onChange={setRecommended} />
      </div>
    </EditorShell>
  )
}

/** Visits · Price · Cost 🔒 · Margin — the last two internal, on the tint (§9.2). */
function FooterRow({ name, cells, bold, internal, lock, warn, pad }: {
  name: string
  cells: string[]
  bold?: boolean
  internal?: boolean
  lock?: boolean
  warn?: boolean[]
  /** The rows' ⋮ column, empty here, so the figures stay under their choices. */
  pad?: boolean
}) {
  return (
    <tr className={cn('border-b border-line-soft last:border-b-0', internal && 'bg-surface-internal')}>
      <td className={cn('sticky left-0 z-10 h-[49px] px-5 text-[14px] text-text-muted',
        internal ? 'bg-surface-internal' : 'bg-surface')}>
        <span className="inline-flex items-center gap-1.5">
          {name}
          {lock && <IconLock size={12} className="text-text-faint" />}
        </span>
      </td>
      {cells.map((text, index) => (
        <td key={index}
          className={cn('tnum px-3 text-center text-[14px]',
            bold ? 'font-bold text-text' : internal && !lock ? 'font-semibold text-text' : 'text-text-body',
            lock && 'text-text-muted',
            warn?.[index] && '!text-warn')}>
          {text}
        </td>
      ))}
      {pad && <td aria-hidden className={cn('w-11', internal && 'bg-surface-internal')} />}
    </tr>
  )
}

